/* eslint-disable react/function-component-definition */
import { useState } from "react";
import api from "api/api";
import { sortAccountRows } from "utils/accountSort";
import { fetchAccountListByName } from "api/accountQueryApi";

// 숫자 파싱
const parseNumber = (value) => {
  if (!value) return 0;
  return Number(String(value).replace(/,/g, "")) || 0;
};

// 숫자 포맷
const formatNumber = (value) => {
  if (!value && value !== 0) return "";
  return Number(value).toLocaleString();
};

export default function useDeadlineBalanceData(year, month, delYn) {
  const [balanceRows, setBalanceRows] = useState([]);
  const [depositRows, setDepositRows] = useState([]);
  const [accountList, setAccountList] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ 왼쪽 테이블 조회
  const fetchDeadlineBalanceList = async () => {
    setLoading(true);
    try {
      const res = await api.get("/Account/AccountDeadlineBalanceList", {
        params: { year, month, del_yn: delYn },
      });

      const rows = (res.data || []).map((item) => ({
        account_id: item.account_id,
        account_name: item.account_name,
        year: item.year,
        month: item.month,
        balance_file: item.balance_file,
        living_cost: formatNumber(item.living_cost),
        basic_cost: formatNumber(item.basic_cost),
        employ_cost: formatNumber(item.employ_cost),
        integrity_cost: formatNumber(item.integrity_cost),
        input_exp: item.input_exp,
        balance_price: formatNumber(item.balance_price),
        before_price: formatNumber(item.before_price),
        before_price2: parseNumber(item.before_price2),
      }));

      // ✅ 거래처 행은 기본적으로 거래처명 기준으로 정렬해서 화면 일관성 유지
      setBalanceRows(sortAccountRows(rows, { sortKey: "account_name", keepAllOnTop: true }));
    } catch (err) {
      console.error("DeadlineBalanceList 조회 실패:", err);
      setBalanceRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ 가운데 테이블 조회
  const fetchDepositHistoryList = async (account_id) => {
    setLoading(true);
    try {
      const res = await api.get("/Account/AccountDepositHistoryList", {
        params: { account_id, year, month },
      });

      const rows = (res.data || []).map((item) => {
        const note = item.note || "";
        const rawType = String(item.type || "").trim();
        const isRefund = rawType === "6" || rawType === "환불" || note.includes("[환불]");

        return {
          account_id: item.account_id,
          // ✅ 미수기준일 계산용 history year/month 원본값 유지
          year: item.year,
          month: item.month,
          type_code: item.type_code, // ✅ UPDATE 식별용 숫자 코드
          // ✅ 백엔드 라벨이 '미수잔액'으로 내려와도 환불 태그가 있으면 환불로 표기
          type: isRefund ? "환불" : rawType,
          input_dt: item.input_dt,
          balance_dt: item.balance_dt,
          difference_price: parseNumber(item.difference_price),
          input_price: parseNumber(item.input_price),
          deposit_amount: parseNumber(item.deposit_amount),
          note,
        };
      });
      // 미수기준일(연/월) 오름차순 -> 입금일자 오름차순
      rows.sort((a, b) => {
        const yearDiff = Number(a?.year || 0) - Number(b?.year || 0);
        if (yearDiff !== 0) return yearDiff;
        const monthDiff = Number(a?.month || 0) - Number(b?.month || 0);
        if (monthDiff !== 0) return monthDiff;
        return String(a?.input_dt || "").localeCompare(String(b?.input_dt || ""));
      });
      setDepositRows(rows);
    } catch (err) {
      console.error("DepositHistoryList 조회 실패:", err);
      setDepositRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ 계정 목록 조회
  const fetchAccountList = async () => {
    try {
      // ✅ 공통 조회 API: 거래처명 기준 정렬이 적용된 목록 반환
      const rows = await fetchAccountListByName({ accountType: "0" });
      setAccountList(rows);
    } catch (err) {
      console.error("AccountList 조회 실패:", err);
      setAccountList([]);
    }
  };

  // ✅ 차액 조회 함수 (selectbox 변경 시 호출)
  const fetchAccountDeadlineDifferencePriceSearch = async (account_id, year, month, type) => {
    try {
      const res = await api.get("/Account/AccountDeadlineDifferencePriceSearch", {
        params: { account_id, year, month, type },
      });

      // 예: [{difference_price: 120000}] 형태 가정
      if (res.data && res.data.length > 0) {
        return parseNumber(res.data[0].difference_price);
      }
      return null;
    } catch (err) {
      console.error("DeadlineDifferencePriceSearch 실패:", err);
      return null;
    }
  };

  return {
    balanceRows,
    depositRows,
    accountList,
    loading,
    fetchDeadlineBalanceList,
    fetchDepositHistoryList,
    fetchAccountList,
    fetchAccountDeadlineDifferencePriceSearch, // ✅ export 추가
    setBalanceRows,
    setDepositRows,
  };
}

export { parseNumber, formatNumber };
